import { unionBy } from "lodash";

import { HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  Button,
  Checkbox,
  Chip,
  ClickAwayListener,
  Drawer,
  IconButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import { useMediaQuery } from "@mui/material";

import { useConfirmDialog } from "@/baseComponents";
import { AIRPORT_MAP, AirportMapItem } from "@/constants/airport";
import { SEARCH_DATE_RANGE_PICKER_OPEN } from "@/constants/eventName";
import { CITY_IMG } from "@/constants/image-map";
import { FREE_FLIGHT_RANGE, MAX_FLIGHT_RANGE } from "@/constants/membership";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import { useListenEvent } from "@/hooks/useEventEmitter";
import { useMultiCityCodes } from "@/hooks/useMultiCityCodes";
import usePlatform from "@/hooks/usePlatform";
import { convertString, filterAirport } from "@/utils/index";
import ProChip from "@/components/ProChip";

interface SearchAirportProps {
  placeholder: string;
  Icon?: React.ReactNode;
  selectData: AirportMapItem[];
  onChange: (data: AirportMapItem[]) => void;
  maxCount?: number;
  showTip?: boolean;
}

const SearchAirport = ({
  placeholder,
  Icon,
  selectData,
  onChange,
  maxCount = 3,
  showTip = true,
}: SearchAirportProps) => {
  const anchorElRef = useRef(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { isMobile } = usePlatform();
  const [open, setOpen] = useState(false);
  const [openDrawer, setDrawer] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const minLimitTags = useMediaQuery("(max-width:820px)");
  const maxLimitTags = useMediaQuery("(min-width:980px)");

  const store = useGlobalStoreContext();
  const showUpsell = store?.persistStore?.show_upsell;

  const confirm = useConfirmDialog();

  console.log("SearchHotel", maxCount);

  useListenEvent(SEARCH_DATE_RANGE_PICKER_OPEN, () => {
    setOpen(false);
  });

  const { combinedCodes: multiCityCodes } = useMultiCityCodes();

  const options = useMemo(() => {
    const keyword = inputValue.trim().toUpperCase();

    if (keyword.length === 0) {
      return [];
    }

    return filterAirport(keyword, { enableCustomCode: true, multiCityCodes });
  }, [inputValue, multiCityCodes]);

  const handleOpenClick = useEvent(() => {
    if (isMobile) {
      setDrawer((pre) => !pre);
    } else {
      setOpen((pre) => !pre);
    }
  });

  const handleChange = useEvent((e: React.SyntheticEvent, data: Array<AirportMapItem | string>) => {
    if (selectData.length <= maxCount) {
      onChange(data as AirportMapItem[]);
    } else {
      if (selectData.length > data.length) {
        onChange(data as AirportMapItem[]);
      }
    }
  });

  const getKey = useCallback((data: AirportMapItem) => {
    const info = AIRPORT_MAP[data.iata];

    return `${data.iata}-${info?.country || ""}-${info?.city || ""}`;
  }, []);

  useEffect(() => {
    if (open) {
      inputRef.current && inputRef.current.focus();
    } else {
      setInputValue("");
    }
  }, [open]);

  useEffect(() => {
    if (maxCount < selectData?.length) {
      onChange(selectData.slice(0, maxCount));
    }
  }, [maxCount]);

  useEffect(() => {
    if (!openDrawer) {
      setInputValue("");
    }
  }, [openDrawer]);

  const limitTags = useMemo(() => {
    if (isMobile) return 3;

    if (maxLimitTags) return 3;
    if (minLimitTags) return 1;

    return 2;
  }, [isMobile, maxLimitTags, minLimitTags]);

  const renderOption = useEvent(
    (props: HTMLAttributes<HTMLElement>, option: AirportMapItem | string) => {
      if (typeof option === "string") {
        const multiCityItem = multiCityCodes.find((item) => item.code === option);
        const airports = multiCityItem?.airports ?? [];
        const description = multiCityItem?.description ?? "";
        return (
          <li
            {...props}
            style={{ padding: "4px", paddingLeft: "6px" }}
            key={option}
            onClick={async (e) => {
              e.stopPropagation();
              let limitExceededType = 0;
              const airports = multiCityCodes.find((item) => item.code === option)?.airports ?? [];
              // 使用Set去重后转为数组
              const reduceSelectData = unionBy(
                [...selectData, ...airports.map((ele) => AIRPORT_MAP[ele])],
                "iata"
              );
              if (airports.length > maxCount) {
                limitExceededType = 1;
              } else if (reduceSelectData.length > maxCount) {
                limitExceededType = 2;
              }

              console.log("limitExceededType", limitExceededType);

              if (reduceSelectData.length > maxCount) {
                await confirm({
                  title: "",
                  content: (
                    <div className="w-[500px] flex flex-col items-center justify-center text-center">
                      {showUpsell && (
                        <Typography color="primary" fontSize={18} fontWeight={600} className="mb-2">
                          <ProChip marginBottom="2px" /> Feature
                        </Typography>
                      )}
                      <Typography fontSize={18} className="!mb-4">
                        {showUpsell
                          ? "You've reached limited entries. Upgrade to PRO to enjoy 32-entry search."
                          : "You’ve reached the entry limit. Please adjust your search to stay within the 32-entry maximum."}
                      </Typography>
                      <Typography fontSize={18} className="!mb-4">
                        This selection includes <strong>{airports.length}</strong>{" "}
                        {airports.length === 1 ? "airport" : "airports"}, but only{" "}
                        <strong>{maxCount}</strong> are allowed to reach the limit. Consider
                        narrowing your airport list or changing the date range.
                      </Typography>
                      <Typography fontSize={18} className="!mb-4">
                        Or you can try Panorama for searching with unlimited entries.
                      </Typography>
                      <div className="flex justify-center">
                        {showUpsell ? (
                          <Button
                            variant="contained"
                            color="primary"
                            className="w-[200px]"
                            sx={{ borderRadius: "18px" }}
                            onClick={() => window.open("/pro", "_blank")}
                          >
                            Upgrade Now
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ),
                  showCancel: false,
                  showConfirm: true,
                });

                return;
              }

              onChange(reduceSelectData);

              inputRef.current?.blur();

              setTimeout(() => {
                setOpen(false);
                setDrawer(false);
              }, 0);
            }}
          >
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
              checked={false}
            />

            <div className="h-[33px] w-[40px] mr-[4px] flex-shrink-0 flex items-center justify-center">
              <AutoAwesomeIcon color="primary" className="!text-[25px] text-primary" />
            </div>

            {/* <div className="flex text-xs">
              <div className="pl-[4px]">{option}</div>
              <div className="pl-[4px]">{airports.join(', ')}</div>
            </div> */}
            <div className="pl-[4px]">
              <div className="flex text-sm">
                {option} {description ? `(${description})` : ""}
              </div>
              <div className="flex text-xs">{[...airports].sort().join(", ")}</div>
            </div>
          </li>
        );
      }

      const selected = selectData.findIndex((ele) => getKey(ele) === getKey(option)) !== -1;

      const city = convertString(option.city).trim().toLowerCase().replace(/\s+/g, "_");
      const isExist = CITY_IMG.includes(city);

      return (
        <li
          {...props}
          style={{ padding: "4px", paddingLeft: "6px" }}
          key={getKey(option)}
          onClick={(e) => {
            e.stopPropagation();

            if (selected) {
              onChange(selectData.filter((ele) => getKey(ele) !== getKey(option)));
            } else {
              onChange([...selectData, option].slice(-maxCount));

              inputRef.current?.blur();

              setTimeout(() => {
                setOpen(false);
                setDrawer(false);
              }, 0);
            }
          }}
        >
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon />}
            checkedIcon={<CheckBoxIcon />}
            checked={selected}
          />

          <div className="h-[33px] w-[40px] mr-[4px] flex-shrink-0 flex items-center justify-center">
            {isExist ? (
              <img
                className="h-[33px] w-[40px] object-cover border border-gray-300 rounded"
                src={`https://img.awardtool-api.com/city_imgs/${city}.jpg`}
                alt=""
              />
            ) : (
              <FlightTakeoffIcon className="!text-[25px]" />
            )}
          </div>

          <div className="pl-[4px]">
            <div className="flex text-sm">
              {convertString(option.name).trim()}
              {`  (${convertString(option.iata).trim()})`}
            </div>
            <div className="flex text-xs">
              {option.city ? convertString(option.city).trim() + ", " : ""}
              {option.subd ? convertString(option.subd).trim() : ""}
              {option.country ? ", " + convertString(option.country) : ""}
            </div>
          </div>
        </li>
      );
    }
  );

  const renderMaximumTip = useCallback(() => {
    const show_upsell = store?.show_upsell;

    if (!showTip) {
      return null;
    }
    return selectData.length >= maxCount ? (
      <div>
        {show_upsell ? (
          <Typography className="py-[5px] pl-[12px] " variant="body2" style={{ color: "#f44336" }}>
            {"You've reached limited entries. Select up to " +
              FREE_FLIGHT_RANGE +
              " pairs of airports for one day, 1 pair of airports for " +
              FREE_FLIGHT_RANGE +
              " days."}
            <Typography sx={{ fontWeight: "bold" }} variant="body2" color="primary">
              {"Upgrade to PRO to enjoy " + MAX_FLIGHT_RANGE + "-entry search."}
            </Typography>
          </Typography>
        ) : (
          <Typography className="py-[5px] pl-[12px] " variant="body2" style={{ color: "#f44336" }}>
            <Typography variant="body2" color="primary">
              You've reached limited entries.
            </Typography>
          </Typography>
        )}
      </div>
    ) : (
      <Typography className="py-[5px] pl-[12px] " variant="body2" style={{ color: "#f44336" }}>
        <Typography sx={{ fontWeight: "600" }} variant="body2" color="primary">
          You can select up to {maxCount} airports.
        </Typography>
      </Typography>
    );
  }, [store?.show_upsell, showTip, selectData.length, maxCount]);

  const renderTags = useCallback(
    (value: (AirportMapItem | string)[], getTagProps: AutocompleteRenderGetTagProps) =>
      value.map((option, index) => {
        if (typeof option !== "string") {
          return (
            <Chip
              label={option.iata}
              {...getTagProps({ index })}
              key={option.iata}
              sx={{
                "& .MuiChip-label": {
                  paddingRight: "8px",
                },
              }}
              deleteIcon={
                <IconButton size="small" sx={{ width: "21px", height: "21px" }}>
                  <CloseIcon className="!text-[16px]" />
                </IconButton>
              }
            />
          );
        }
      }),
    []
  );

  const renderPcAutocomplete = () => {
    return isMobile ? null : (
      <Autocomplete
        multiple
        disablePortal
        openOnFocus
        clearOnBlur={false}
        disableCloseOnSelect
        options={options}
        value={selectData}
        onChange={handleChange}
        freeSolo
        filterOptions={() => options}
        PaperComponent={(props: HTMLAttributes<HTMLElement>) => (
          <Paper
            {...props}
            elevation={8}
            style={{
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              width: "120%",
            }}
          >
            {renderMaximumTip()}
            {props.children}
          </Paper>
        )}
        getOptionLabel={(option: AirportMapItem | string) =>
          typeof option === "string" ? option : convertString(option.iata)
        }
        renderOption={renderOption}
        renderTags={renderTags}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus={false}
            variant="outlined"
            placeholder={selectData.length ? "" : placeholder}
            inputRef={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        )}
      />
    );
  };

  const renderMobileAutocomplete = () => {
    return isMobile ? (
      <Autocomplete
        multiple
        disablePortal
        openOnFocus
        popupIcon={null}
        clearOnBlur={false}
        disableCloseOnSelect
        options={options}
        value={selectData}
        freeSolo
        onChange={handleChange}
        filterOptions={() => options}
        PaperComponent={(props: HTMLAttributes<HTMLElement>) => (
          <Paper {...props} elevation={1} style={{ boxShadow: "none", height: "100%" }}>
            {renderMaximumTip()}
            {props.children}
          </Paper>
        )}
        ListboxProps={{ style: { maxHeight: "100vh" } }}
        sx={{
          "& .MuiInputBase-root": {
            padding: "10px 0 10px 48px",
            background: "transparent",
          },
          "& :before": {
            opacity: 0.4,
          },
        }}
        getOptionLabel={(option: AirportMapItem | string) =>
          typeof option === "string" ? option : convertString(option.iata)
        }
        renderOption={renderOption}
        renderTags={renderTags}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            inputRef={inputRef}
            variant="filled"
            placeholder={selectData.length ? "" : placeholder}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        )}
      />
    ) : null;
  };

  return (
    <>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <div className="w-full relative" onClick={handleOpenClick}>
          <div ref={anchorElRef} className="w-full" />

          <Autocomplete
            fullWidth
            multiple
            disablePortal
            disableCloseOnSelect
            clearOnBlur={false}
            limitTags={limitTags}
            options={[]}
            value={selectData}
            popupIcon={null}
            sx={{
              "& .MuiInputBase-root": {
                paddingRight: "0px !important",
              },
              "& .MuiInputBase-input": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                visibility: "hidden",
                width: "1px",
                minWidth: "1px !important",
              },
              "& .MuiAutocomplete-popper": {
                display: "none",
              },
            }}
            PaperComponent={() => <div className="hidden" />}
            onChange={handleChange}
            renderTags={renderTags}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  style: { paddingLeft: "4px" },
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <IconButton size="small">{Icon}</IconButton>
                      {params.InputProps.startAdornment ? (
                        params.InputProps.startAdornment
                      ) : (
                        <Typography color="#909092">{placeholder}</Typography>
                      )}
                    </>
                  ),
                }}
              />
            )}
            getOptionLabel={(option: AirportMapItem) => convertString(option.iata)}
            renderOption={() => null}
          />

          {/* pc desktop */}
          <Popper
            open={open}
            disablePortal
            placement="bottom-start"
            anchorEl={anchorElRef.current}
            sx={{ zIndex: 999, width: "100%" }}
            id={open ? `city-popper-${placeholder}` : undefined}
          >
            <Paper elevation={8} onClick={(e) => e.stopPropagation()}>
              {renderPcAutocomplete()}
            </Paper>
          </Popper>
        </div>
      </ClickAwayListener>

      {/* mobile */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setDrawer(false)}
        PaperProps={{
          elevation: 1,
          style: { width: "100%" },
        }}
      >
        <div className="relative h-full overflow-hidden">
          <div className="h-full">{renderMobileAutocomplete()}</div>

          <div className="absolute top-0 left-0 h-[58px] w-[50px] flex items-center justify-center z-[100px]">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setDrawer(false);
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default SearchAirport;
